// Product management page for admin/testing CRUD operations
import React, { useState, useEffect, useMemo } from 'react';
import { useProducts, useProductManagement, useStocks } from '../hooks';
import { Product, Stock, ProductCategory } from '../types';
import { getCategoryLabel, CATEGORIES } from '../shared/categories';
import { createAdminHeaders } from '../shared/authConfig';
import ProductForm from './ProductForm';
import CategoryFilter from './CategoryFilter';
import './ProductManagement.css';
import { apiService } from '../services/api';

const ProductManagement: React.FC = () => {
    const { data: allProducts, loading, error, refetch } = useProducts();
    const { data: stocks, loading: stocksLoading, error: stocksError, refetch: refetchStocks } = useStocks();
    const {
        createProduct,
        updateProduct,
        deleteProduct,
        duplicateProduct,
        isSubmitting,
        submitError
    } = useProductManagement();

    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [isSeeding, setIsSeeding] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<ProductCategory | 'all'>('all');
    const [showUnavailable, setShowUnavailable] = useState(false);
    const [overlayMouseDownTarget, setOverlayMouseDownTarget] = useState<EventTarget | null>(null);

    // Filter products based on selected category and availability, then sort by category order
    const products = useMemo(() => {
        if (!allProducts) return allProducts;

        let filtered = allProducts;

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(p => p.category === selectedCategory);
        }

        // Filter by availability (hide unavailable by default)
        if (!showUnavailable) {
            filtered = filtered.filter(p => p.available);
        }

        // Sort by category order (based on CATEGORIES array), then by sortOrder within category
        return filtered.sort((a, b) => {
            // Get category index from CATEGORIES array
            const aCategoryIndex = CATEGORIES.findIndex(cat => cat.value === a.category);
            const bCategoryIndex = CATEGORIES.findIndex(cat => cat.value === b.category);

            // First sort by category order
            if (aCategoryIndex !== bCategoryIndex) {
                return aCategoryIndex - bCategoryIndex;
            }

            // Within same category, sort by sortOrder
            const aSortOrder = a.sortOrder ?? 999999;
            const bSortOrder = b.sortOrder ?? 999999;

            return aSortOrder - bSortOrder;
        });
    }, [allProducts, selectedCategory, showUnavailable]);

    // Get available categories from all products
    const availableCategories = useMemo(() => {
        if (!allProducts) return [];
        const productCategories = new Set(allProducts.map(p => p.category).filter(Boolean));
        return CATEGORIES
            .map(cat => cat.value)
            .filter(cat => productCategories.has(cat));
    }, [allProducts]);

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showForm && !isSubmitting) {
                handleCancelForm();
            }
        };

        if (showForm) {
            document.addEventListener('keydown', handleEscape);
            // Prevent background scrolling when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [showForm, isSubmitting]);

    const handleCreate = async (productData: Omit<Product, 'id' | 'availableStock' | 'stockBreakdown'>) => {
        try {
            await createProduct(productData);
            setShowForm(false);
            await Promise.all([refetch(), refetchStocks()]);
        } catch (error) {
            // Error is handled by the hook
        }
    };

    const handleUpdate = async (productData: Omit<Product, 'id' | 'availableStock' | 'stockBreakdown'>) => {
        if (!editingProduct) return;

        try {
            await updateProduct(editingProduct.id, productData);
            setEditingProduct(null);
            setShowForm(false);
            await Promise.all([refetch(), refetchStocks()]);
        } catch (error) {
            // Error is handled by the hook
        }
    };

    const handleDelete = async (id: string) => {
        if (deleteConfirm !== id) {
            setDeleteConfirm(id);
            return;
        }

        try {
            await deleteProduct(id);
            setDeleteConfirm(null);
            refetch();
        } catch (error) {
            // Error is handled by the hook
        }
    };

    const handleDuplicate = async (id: string) => {
        try {
            await duplicateProduct(id);
            refetch();
        } catch (error) {
            // Error is handled by the hook
        }
    };

    const handleEdit = (product: Product) => {
        setEditingProduct(product);
        setShowForm(true);
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingProduct(null);
    };

    const handleOverlayMouseDown = (event: React.MouseEvent) => {
        setOverlayMouseDownTarget(event.target);
    };

    const handleOverlayClick = (event: React.MouseEvent) => {
        // Only close if both mousedown and mouseup happened on the overlay
        if (event.target === overlayMouseDownTarget && event.target === event.currentTarget) {
            handleCancelForm();
        }
        setOverlayMouseDownTarget(null);
    };

    const resolvedStocks = stocks ?? [];
    const stockMap = React.useMemo(() => {
        const map = new Map<string, Stock>();
        resolvedStocks.forEach(stock => {
            map.set(stock.id, stock);
        });
        return map;
    }, [resolvedStocks]);

    const handleStockUpdates = async (updates: Array<{ stockId: string; quantity: number }>) => {
        if (updates.length === 0) return;

        await Promise.all(updates.map(async ({ stockId, quantity }) => {
            await apiService.updateStock(stockId, { quantity });
        }));

        await refetchStocks();
    };

    const getStockBadge = (product: Product) => {
        if (!product.stockIds || product.stockIds.length === 0) {
            return <span className="stock-badge untracked">Not linked</span>;
        }

        const availableStock = product.availableStock ?? null;

        if (availableStock === null) {
            return <span className="stock-badge untracked">Not tracked</span>;
        }

        if (availableStock <= 0) {
            return <span className="stock-badge out-of-stock">Out of stock</span>;
        }

        if (availableStock <= 5) {
            return <span className="stock-badge low-stock">Low ({availableStock})</span>;
        }

        return <span className="stock-badge in-stock">In stock ({availableStock})</span>;
    };

    const getLinkedStockNames = (product: Product) => {
        if (!product.stockIds || product.stockIds.length === 0) {
            return '—';
        }

        const names = product.stockIds
            .map(id => stockMap.get(id)?.name || id)
            .join(', ');

        return `Linked: ${names}`;
    };

    const handleSeedProducts = async () => {
        setIsSeeding(true);
        try {
            const headers = createAdminHeaders();

            const response = await fetch('/api/products/seed', {
                method: 'POST',
                headers,
            });

            if (!response.ok) {
                throw new Error('Failed to seed products');
            }

            await Promise.all([refetch(), refetchStocks()]);
        } catch (error) {
            console.error('Seed failed:', error);
        } finally {
            setIsSeeding(false);
        }
    };

    if (loading) {
        return (
            <div className="product-management">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading products...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="product-management">
            <div className="management-header">
                <h1>Product Management</h1>
                <p>Manage your restaurant's menu items</p>

                <div className="header-actions">
                    {!showForm && (
                        <button
                            className="add-product-btn"
                            onClick={() => setShowForm(true)}
                        >
                            + Add New Product
                        </button>
                    )}

                    {(!allProducts || allProducts.length === 0) && !loading && (
                        <button
                            className="seed-btn"
                            onClick={handleSeedProducts}
                            disabled={isSeeding}
                        >
                            {isSeeding ? 'Seeding...' : '🌱 Seed Sample Products'}
                        </button>
                    )}
                </div>

                {allProducts && allProducts.length > 0 && (
                    <div className="filter-options">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={showUnavailable}
                                onChange={(e) => setShowUnavailable(e.target.checked)}
                            />
                            <span>Show Unavailable Items</span>
                        </label>
                    </div>
                )}
            </div>

            {/* Category Filter */}
            {allProducts && allProducts.length > 0 && (
                <div className="category-filter-section">
                    <CategoryFilter
                        selectedCategory={selectedCategory}
                        onCategoryChange={setSelectedCategory}
                        availableCategories={availableCategories}
                    />
                </div>
            )}

            {error && (
                <div className="error-message">
                    <p>Error loading products: {error}</p>
                    <button onClick={refetch} className="retry-button">
                        Retry
                    </button>
                </div>
            )}

            {submitError && (
                <div className="error-message">
                    <p>Operation failed: {submitError}</p>
                </div>
            )}

            {stocksLoading && (
                <div className="info-banner">
                    <p>Loading stock records…</p>
                </div>
            )}

            {stocksError && (
                <div className="error-message">
                    <p>Failed to load stock data: {stocksError}</p>
                    <button onClick={refetchStocks} className="retry-button">
                        Retry
                    </button>
                </div>
            )}

            {/* Modal for Product Form */}
            {showForm && (
                <div
                    className="modal-overlay"
                    onMouseDown={handleOverlayMouseDown}
                    onClick={handleOverlayClick}
                >
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
                            <button
                                className="modal-close-btn"
                                onClick={handleCancelForm}
                                disabled={isSubmitting}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modal-body">
                            <ProductForm
                                initialData={editingProduct}
                                stocks={resolvedStocks}
                                stocksLoading={stocksLoading}
                                onSubmit={editingProduct ? handleUpdate : handleCreate}
                                onUpdateStocks={handleStockUpdates}
                                onRefreshStocks={refetchStocks}
                                onCancel={handleCancelForm}
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    </div>
                </div>
            )}

            {products && products.length > 0 && (
                <div className="products-table-section">
                    <h2>Current Products ({products.length})</h2>
                    <div className="products-table">
                        <div className="table-header">
                            <div className="col-image">Image</div>
                            <div className="col-name">Name</div>
                            <div className="col-category">Category</div>
                            <div className="col-price">Price</div>
                            <div className="col-sort">Sort</div>
                            <div className="col-stock">Stock</div>
                            <div className="col-status">Status</div>
                            <div className="col-actions">Actions</div>
                        </div>

                        {products.map((product) => (
                            <div key={product.id} className="table-row">
                                <div className="col-image">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="product-thumbnail"
                                    />
                                </div>
                                <div className="col-name">
                                    <strong>{product.name}</strong>
                                    <p className="product-description-short">
                                        {product.description.length > 50
                                            ? `${product.description.substring(0, 50)}...`
                                            : product.description
                                        }
                                    </p>
                                </div>
                                <div className="col-category">
                                    <span className={`category-badge category-${product.category}`}>
                                        {getCategoryLabel(product.category as ProductCategory)}
                                    </span>
                                </div>
                                <div className="col-price">
                                    £{product.price.toFixed(2)}
                                </div>
                                <div className="col-sort">
                                    <span className="sort-order-badge">
                                        {product.sortOrder ?? 0}
                                    </span>
                                </div>
                                <div className="col-stock">
                                    {getStockBadge(product)}
                                    <div className="stock-links">
                                        {getLinkedStockNames(product)}
                                    </div>
                                </div>
                                <div className="col-status">
                                    <span className={`status-badge ${product.available ? 'available' : 'unavailable'}`}>
                                        {product.available ? 'Available' : 'Unavailable'}
                                    </span>
                                </div>
                                <div className="col-actions">
                                    <button
                                        className="edit-btn"
                                        onClick={() => handleEdit(product)}
                                        disabled={isSubmitting}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        className="duplicate-btn"
                                        onClick={() => handleDuplicate(product.id)}
                                        disabled={isSubmitting}
                                        title="Duplicate this product"
                                    >
                                        Duplicate
                                    </button>
                                    <button
                                        className={`delete-btn ${deleteConfirm === product.id ? 'confirm' : ''}`}
                                        onClick={() => handleDelete(product.id)}
                                        disabled={isSubmitting}
                                    >
                                        {deleteConfirm === product.id ? 'Confirm?' : 'Delete'}
                                    </button>
                                    {deleteConfirm === product.id && (
                                        <button
                                            className="cancel-delete-btn"
                                            onClick={() => setDeleteConfirm(null)}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {products && products.length === 0 && !loading && (
                <div className="empty-products">
                    <div className="empty-icon">📦</div>
                    <h3>No products yet</h3>
                    <p>Add your first product to get started!</p>
                </div>
            )}
        </div>
    );
};

export default ProductManagement;
